import React, { FC } from 'react';

import { isMobile } from 'react-device-detect';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContentModule } from '@nhl/nhl-react-ui/lib/ContentModule';
import ReactTable from '@nhl/nhl-react-ui/lib/NhlTable/ReactTable';
import { aggregateRows } from '@nhl/nhl-react-ui/lib/NhlTable/utils/aggregate';

import '../../../../styles/common/react-table.scss';
import { ColumnDefinition, Sort } from '../../../../types/ReactTableTypes';
import { Record } from '../../../../types/RecordCategoriesTypes';
import { Option } from '../../../../types/Types';
import TemplateKeys from '../../../../utils/contentModuleTemplateKeys';
import { SubNav, NavListSelect } from '../../../components';
import { sortAndRankData } from '../../../pages/allStar/components/allStarRecords/utils';
import { FranchiseTombstone } from '../../../pages/franchise/components/franchiseTombstone';
import { RecordOptions } from '../types';

import enhanceRecords from './utils/enhanceRecords';
import { PIVOT_BY, PIVOT_BY_SORTS } from './utils/pivotBy';

import './styles.scss';

type Props = {
    columns: ColumnDefinition[];
    pageSize: number;
    showPagination: boolean;
    sortData: Sort[];
    data: any[];
    options?: RecordOptions;
    record: Record;
    recordSelectOptions: Option[];
    recordTypeSelectOptions: Option[];
    templateKey: string;
    selectedCategoryValue: any;
    selectedRecordTypeValue: any;
    setVideoId: any;
};

const TEAM_RECORD_CATEGORY = 4;
const PLAYOFF_TEAM_RECORD_CATEGORY = 7;
const HEAD_COACH_RECORD_CATEGORY = 8;
const PLAYOFF_HEAD_COACH_RECORD_CATEGORY = 11;
const STANLEY_CUP_FINALS_TEAM_RECORD_CATEGORY = 24;

const RecordTable: FC<Props> = ({
    columns,
    pageSize,
    showPagination,
    sortData,
    data,
    options,
    record,
    recordSelectOptions,
    recordTypeSelectOptions,
    templateKey,
    selectedCategoryValue,
    selectedRecordTypeValue,
    setVideoId,
}) => {
    const { pathname } = window.location;
    const pageName = pathname.slice(pathname.lastIndexOf('/') + 1);
    const pivotBy = PIVOT_BY[templateKey]?.[pageName];
    const pivotBySorts = PIVOT_BY_SORTS[templateKey];

    const isTeamRecords =
        record.recordCategory.id === TEAM_RECORD_CATEGORY ||
        record.recordCategory.id === PLAYOFF_TEAM_RECORD_CATEGORY ||
        record.recordCategory.id === STANLEY_CUP_FINALS_TEAM_RECORD_CATEGORY;
    const isCoachRecords =
        record.recordCategory.id === HEAD_COACH_RECORD_CATEGORY ||
        record.recordCategory.id === PLAYOFF_HEAD_COACH_RECORD_CATEGORY;
    const isBosTorGame2Tie =
        pageName === 'playoff-franchise-game-by-game-records' &&
        (window.recordsSettings.franchiseTricode === 'bos' || window.recordsSettings.franchiseTricode === 'tor');
    const selectedOption = recordTypeSelectOptions.find((x) => x.value === record.recordTypeSlug) || {
        label: record.recordTypeSlug,
        value: record.recordTypeSlug,
    };

    if (pivotBy) {
        data = aggregateRows(data, pivotBy, columns);
        sortAndRankData(data, pivotBySorts);
    }

    return (
        <div className="records-category-container">
            <ContentModule
                className="hero-module"
                baseUrl={window.recordsSettings.franchiseScopedRestUrl}
                templateKey={record.templateKey || TemplateKeys[templateKey]}
                sequence={1}
                isPublic
            />

            {window.recordsSettings.franchiseTricode !== 'nhl' ? <FranchiseTombstone /> : null}

            <SubNav>
                {options?.subNav}
                <NavListSelect
                    label={'Record Type'}
                    selected={selectedOption}
                    availableOptions={recordTypeSelectOptions}
                    selectFunction={selectedRecordTypeValue}
                    forceDropDown={true}
                    className={'type-select'}
                />
                <NavListSelect
                    label={'Record'}
                    selected={{
                        label: record.description,
                        value: record.descriptionKey,
                    }}
                    availableOptions={recordSelectOptions}
                    selectFunction={selectedCategoryValue}
                    forceDropDown={true}
                    className={'category-select'}
                />
            </SubNav>

            <div className="page-header">
                <h2>{record.recordCategory.description}</h2>
                <div className="records-sub-header">
                    {record.recordToFranchise[0].videoId !== null ? (
                        <div className="records-category-video-link">
                            <div
                                className="video-button"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setVideoId(record.recordToFranchise[0].videoId);
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={solid('film')}
                                    color={'#0183DA'}
                                    style={{ marginRight: 5, height: '1.4em', width: '1.4em' }}
                                />
                                <h3>{record.description}</h3>
                            </div>
                        </div>
                    ) : (
                        <h3>{record.description}</h3>
                    )}

                    {!isMobile && (
                        <div className="records-category-action-links">
                            <a className="print-button" onClick={() => window.print()}>
                                <FontAwesomeIcon
                                    icon={solid('print')}
                                    color={'#0183DA'}
                                    style={{ height: '1.4em', width: '1.4em' }}
                                />
                                Print
                            </a>
                        </div>
                    )}
                </div>
            </div>
            <div className="records-note">{record.recordToFranchise[0].note}</div>
            <div className="records-category-table-content">
                <div className="record-category">
                    <ReactTable
                        className="records-table -striped -highlight"
                        data={enhanceRecords(data, templateKey, pageName)}
                        columns={columns}
                        resizable={false}
                        defaultPageSize={pageSize}
                        defaultSorted={sortData}
                        showPagination={showPagination}
                    />
                    {!isTeamRecords && (
                        <div className="legend">
                            *Active {isCoachRecords ? 'head coaches' : 'players'} indicated in <strong>bold</strong>
                        </div>
                    )}
                    {isBosTorGame2Tie && (
                        <div className="legend">
                            *
                            <a href={`/playoff-summary/series-results?season=19501951`} rel="noopener" target="_blank">
                                1951 SF called after one overtime period due to curfew
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecordTable;
